/**
 * timing-rules.js — 野鹤派应期规则表（第一阶段）
 *
 * 本文件只回答“哪条古籍规则适用”，不生成日期、不合并候选，也不做
 * 置信度、远近裁决或主应期排序。后续候选层只能消费这里命中的规则。
 *
 * 取舍说明：
 * - 《增删卜易》原文明确给出候选口径的规则才进入表内；
 * - applies() 只读取 analyze() 已提供的结构化字段；
 * - “休囚且无生扶即不可断”未找到明确原文，暂不写入；
 * - 六冲取最近、六合取较远属于下一阶段裁决，本表只保留原文能支持的
 *   “六冲非久远”与“六合待冲开”。
 *
 * strength 标注依据：direct = 原文直接规定；derived = 原文有明确卦例但
 * 需抽象成通则；inferred = 没有专条、仅由相邻通则推出。拿不准一律降为
 * derived，不向 direct 高估。
 */

import { TRIPLE_COMBINE } from './analyze.js';
import { overcomes } from './hexagram.js';

const STRONG_STATES = new Set(['旺', '相']);
const WEAK_STATES = new Set(['休', '囚', '死']);

const useLines = (result) => result?.useGod?.主爻?.length
  ? result.useGod.主爻
  : result?.useGod?.伏神 ? [result.useGod.伏神] : [];
const someUseLine = (result, predicate) => useLines(result).some(predicate);
const isStrong = (line) => STRONG_STATES.has(line.state);
const isWeak = (line) => WEAK_STATES.has(line.state);
const isUnbroken = (line) => !line.isVoid && !line.isMonthBreak && !line.isDayBreak;
const movingLines = (result) => (result?.yao || []).filter((line) => line.moving);
const harmfulLines = (result) => result?.useGod?.overcomeBy || [];
const hasHarmfulLine = (result) => harmfulLines(result).length > 0;
const hasMovingHarmfulLine = (result) => harmfulLines(result).some((line) => line.moving);
const hasCombinedMovingLine = (result) => (result?.yao || [])
  .some((line) => line.combineState === '合绊');

/** 已经三支齐全的三合局；与“缺一待补”分开，供冲开日期扩展使用。 */
export function completeTripleCombineEvidence(result) {
  const lines = result?.yao || [];
  return TRIPLE_COMBINE.map(({ branches, element }) => {
    const existing = lines.filter((line) => branches.includes(line.branch));
    const present = [...new Set(existing.map(({ branch }) => branch))];
    return {
      element,
      branches: [...branches],
      existing: existing.map(({ position, branch, moving }) => ({ position, branch, moving })),
      qualifies: present.length === 3 && result?.patterns?.includes(`三合${element}局`)
    };
  });
}

const hasCompleteTripleCombine = (result) => (
  completeTripleCombineEvidence(result).some(({ qualifies }) => qualifies)
);

/**
 * 三合缺支的完整证据。原书卦例说的是与所问相关的“官局”，不是任意两支
 * 凑在同一组三合中。因此除“两支且至少一动”外，还要求局五行等于用神
 * 五行（用神局），或局五行克用神五行（忌神局）。
 */
export function partialTripleCombineEvidence(result) {
  const lines = result?.yao || [];
  const useElement = result?.useGod?.用神五行
    || result?.useGod?.主爻?.[0]?.element
    || result?.useGod?.伏神?.element
    || null;
  return TRIPLE_COMBINE.map(({ branches, element }) => {
    const hits = lines.filter((line) => branches.includes(line.branch));
    const present = [...new Set(hits.map((line) => line.branch))];
    const hasMoving = hits.some((line) => line.moving);
    const relation = element === useElement
      ? '用神局'
      : useElement && overcomes(element, useElement)
        ? '忌神局'
        : '无关';
    const missing = branches.filter((branch) => !present.includes(branch));
    return {
      element,
      branches: [...branches],
      existing: hits.map(({ position, branch, moving }) => ({ position, branch, moving })),
      present,
      hasMoving,
      missing,
      relation,
      qualifies: present.length === 2 && hasMoving && relation !== '无关'
    };
  });
}

const hasPartialTripleCombine = (result) => (
  partialTripleCombineEvidence(result).some(({ qualifies }) => qualifies)
);

const source = (chapter, note) => Object.freeze({
  book: '增删卜易',
  chapter,
  note
});

const rule = (group, definition) => Object.freeze({
  group,
  ...definition,
  source: Object.freeze(definition.source)
});

const strongRules = Object.freeze([
  rule('旺相有气', {
    id: 'strong-value-day',
    name: '旺相近事待值',
    condition: '用神旺相且不空不破',
    candidates: '用神地支值日',
    source: source('各门类应期总注章第又二十六', '“静而逢值逢冲”“动而逢合逢值”；近事应之于日时。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => isStrong(line) && isUnbroken(line))
  }),
  rule('旺相有气', {
    id: 'strong-value-month',
    name: '旺相远事待值',
    condition: '用神旺相且不空不破',
    candidates: '用神地支值月',
    source: source('各门类应期总注章第又二十六', '“远事定之以年月，近事应之于日时”。'),
    // 由“逢值”与“远事看年月”两条通则合并推出，保守标为 derived。
    strength: 'derived',
    scale: 'month',
    applies: (result) => someUseLine(result, (line) => isStrong(line) && isUnbroken(line))
  }),
  rule('旺相有气', {
    id: 'strong-static-clash',
    name: '旺相静爻待冲',
    condition: '用神旺相而静',
    candidates: '冲用神之日',
    source: source('各门类应期总注章第又二十六', '“静而逢值逢冲”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => isStrong(line) && !line.moving)
  }),
  rule('旺相有气', {
    id: 'strong-static-clash-month',
    name: '旺相静爻远事待月冲',
    condition: '用神旺相而静，按远事取月',
    candidates: '冲用神之月',
    source: source('各门类应期总注章第又二十六', '“静而逢值逢冲”；“远事定之以年月”。'),
    strength: 'direct',
    scale: 'month',
    applies: (result) => someUseLine(result, (line) => isStrong(line) && !line.moving)
  }),
  rule('旺相有气', {
    id: 'strong-static-clash-year',
    name: '旺相静爻久远待年冲',
    condition: '用神旺相而静，按久远之事取年',
    candidates: '冲用神之年',
    source: source('各门类应期总注章第又二十六', '“静而逢值逢冲”；“远事定之以年月”。'),
    strength: 'direct',
    scale: 'year',
    applies: (result) => someUseLine(result, (line) => isStrong(line) && !line.moving)
  }),
  rule('旺相有气', {
    id: 'strong-moving-combine',
    name: '旺相动爻待合',
    condition: '用神旺相而动',
    candidates: '合用神之日',
    source: source('各门类应期总注章第又二十六', '“动而逢合逢值”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => isStrong(line) && line.moving)
  })
]);

const weakRules = Object.freeze([
  rule('休囚无气', {
    id: 'weak-revive',
    name: '休囚待生旺',
    condition: '用神休囚无气',
    candidates: '生扶用神之日',
    source: source('各门类应期总注章第又二十六', '“衰绝者，遇生遇旺”。原文将休囚无气列为例。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, isWeak)
  })
]);

const specialStateRules = Object.freeze([
  rule('空破墓绝', {
    id: 'void-escape-value',
    name: '旬空待出空',
    condition: '用神旬空',
    candidates: '空亡地支值日',
    source: source('旬空章第二十五', '旺相旬空，出空可用；旬外以出空日为候。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => line.isVoid)
  }),
  rule('空破墓绝', {
    id: 'void-escape-clash',
    name: '旬空待冲空',
    condition: '用神旬空',
    candidates: '冲空之日',
    source: source('旬空章第二十五', '“爻遇旬空，逢冲而有用”；应期总注谓“旬空最爱填冲”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => line.isVoid)
  }),
  rule('空破墓绝', {
    id: 'month-break-fill',
    name: '月破待实破日',
    condition: '用神月破',
    candidates: '月破地支值日',
    source: source('月破章第二十七', '“今日虽破，实破之日则不破”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => line.isMonthBreak)
  }),
  rule('空破墓绝', {
    id: 'month-break-exit',
    name: '月破待出破月',
    condition: '用神月破',
    candidates: '月建不再冲用神的出破之月',
    source: source('月破章第二十七', '“目下虽破，出月则不破”。'),
    strength: 'direct',
    scale: 'month',
    applies: (result) => someUseLine(result, (line) => line.isMonthBreak)
  }),
  rule('空破墓绝', {
    id: 'month-break-combine',
    name: '月破待合破',
    condition: '用神月破',
    candidates: '合月破用神之日',
    source: source('月破章第二十七', '“合之日则不破”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => line.isMonthBreak)
  }),
  rule('空破墓绝', {
    id: 'tomb-open-clash',
    name: '入墓待冲墓',
    condition: '用神入墓',
    candidates: '冲开墓库地支之日',
    source: source('各门类应期总注章第又二十六', '“入三墓，俱喜冲开”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => line.isTomb)
  }),
  rule('空破墓绝', {
    id: 'grave-revive',
    name: '入绝待生旺',
    condition: '用神入绝',
    candidates: '生扶用神之日',
    source: source('各门类应期总注章第又二十六', '“衰绝者，遇生遇旺”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => someUseLine(result, (line) => line.isGrave)
  })
]);

const patternRules = Object.freeze([
  rule('卦象', {
    id: 'six-clash-fast',
    name: '六冲应期不久',
    condition: '本卦或变卦为六冲卦',
    candidates: '候选应期不宜取久远；最近候选的裁决留待下一层',
    source: source('六冲章第二十', '“冲者，散也”；并言六冲“亦非久远之象”。'),
    // “取最近候选”不是专门条文，仅由“非久远之象”推得。
    strength: 'inferred',
    scale: 'day',
    applies: (result) => Boolean(result?.patternFlags?.hasSixClash)
  }),
  rule('卦象', {
    id: 'six-combine-open',
    name: '六合待冲开',
    condition: '本卦或变卦为六合卦',
    candidates: '冲开六合之日；较远候选的裁决留待下一层',
    source: source('各门类应期总注章第又二十六', '“遇六合，亦宜相击”；吉凶必待冲开之日月应之。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => Boolean(result?.patternFlags?.hasSixCombine)
  }),
  rule('卦象', {
    id: 'partial-triple-combine-fill',
    name: '三合缺支待补',
    condition: '三合局见两支、至少一动，且为用神局或克用神的忌神局',
    candidates: '局中所缺地支值日',
    source: source('六合章第十九', '卦例谓“寅午戌三合官局，内少寅字，须待寅日”。'),
    // 原书以官局卦例说明，需从个案推演成一般判定，故不标 direct。
    strength: 'derived',
    scale: 'day',
    applies: hasPartialTripleCombine
  }),
  rule('卦象', {
    id: 'triple-combine-open',
    name: '三合成局待冲开',
    condition: '三合局三支齐全',
    candidates: '冲局中任一地支之日',
    source: source('六合章第十九', '三合既成，原书以“冲开”说明其作用解除；本条仅列各成员受冲日期。'),
    strength: 'derived',
    scale: 'day',
    applies: hasCompleteTripleCombine
  }),
  rule('卦象', {
    id: 'longsheng-value-day',
    name: '用神待长生日',
    condition: '用神五行可查十二长生',
    candidates: '用神五行长生地支值日',
    source: source('六神章第十八', 'zsby-061 记子水用神至申日沉疴复起；按水长生在申列日期候选。'),
    strength: 'derived',
    scale: 'day',
    applies: (result) => useLines(result).some((line) => Boolean(line.element))
  })
]);

const supportReleaseRules = Object.freeze([
  ...['day', 'month', 'year'].map((scale) => rule('作用对象扩展', {
    id: `harmful-line-clash-${scale}`,
    name: `克神待${scale === 'day' ? '日' : scale === 'month' ? '月' : '年'}冲去`,
    condition: '有爻克用神',
    candidates: `冲克神之${scale === 'day' ? '日' : scale === 'month' ? '月' : '年'}`,
    source: source(
      '进神退神章第二十九、囤货卖货章第七十三',
      'zsby-028 以辰年冲去戌土，zsby-046 以寅月冲去申金；均为克用神者被冲去。'
    ),
    strength: 'derived',
    basis: 'derived',
    confirmedBy: Object.freeze(['zsby-028', 'zsby-046']),
    scale,
    applies: hasHarmfulLine
  })),
  rule('作用对象扩展', {
    id: 'harmful-moving-line-combine',
    name: '动克神待合绊',
    condition: '有动爻克用神',
    candidates: '合克神动爻之日',
    source: source('六合章第十九', '“动而逢合，谓之合绊”；zsby-047 以辰日合住酉金动爻。'),
    strength: 'direct',
    basis: 'textual',
    scale: 'day',
    applies: hasMovingHarmfulLine
  }),
  rule('作用对象扩展', {
    id: 'combined-line-release',
    name: '合绊待冲开',
    condition: '动爻当前合绊',
    candidates: '冲合绊爻地支之日',
    source: source('六合章第十九', '动爻逢合为合绊；合绊之爻待冲开合。'),
    strength: 'direct',
    basis: 'textual',
    scale: 'day',
    applies: hasCombinedMovingLine
  })
]);

const transformRules = Object.freeze([
  rule('动变', {
    id: 'transform-void-escape',
    name: '动爻化空待填冲',
    condition: '动爻化空',
    candidates: '变爻出空值日或冲空之日',
    source: source('增删黄金策千金赋章第三十四', '“动而化空，亦不为空，吉凶皆应冲空实空之日”。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => movingLines(result).some((line) => line.transform?.toVoid === true)
  }),
  rule('动变', {
    id: 'transform-advance-value-combine',
    name: '化进神待值合',
    condition: '动爻化进神',
    candidates: '动爻逢值或逢合之日',
    source: source('各门类应期总注章第又二十六', '“化进神，逢值逢合”。原例申化酉，列申日与巳日为候。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => movingLines(result).some((line) => line.transform?.kind === '化进神')
  }),
  rule('动变', {
    id: 'transform-retreat-uncertain',
    name: '化退神应期反复',
    condition: '动爻化退神',
    candidates: '退神值日或冲退神之日；本规则不单定一个应期',
    source: source('各门类应期总注章第又二十六', '“化退神，忌值忌冲”；原文并列值、冲两类应期。'),
    strength: 'direct',
    scale: 'day',
    applies: (result) => movingLines(result).some((line) => line.transform?.kind === '化退神')
  })
]);

/** 按用户指定的用神状态分组；每组及每条规则均只读。 */
export const TIMING_RULE_GROUPS = Object.freeze({
  旺相有气: strongRules,
  休囚无气: weakRules,
  空破墓绝: specialStateRules,
  卦象: patternRules,
  作用对象扩展: supportReleaseRules,
  动变: transformRules
});

/** 扁平规则表，便于后续候选生成层逐条执行。 */
export const TIMING_RULES = Object.freeze(Object.values(TIMING_RULE_GROUPS).flat());

/** 返回当前排盘命中的规则；不生成或排序任何日期候选。 */
export function applicableTimingRules(result) {
  return TIMING_RULES.filter((entry) => entry.applies(result));
}
